// src/components/CharactersList.js
import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import AddCharacterForm from './AddCharacterForm';
import CharacterModal from './CharacterModal';
import { colors } from '../styles/colors';

const CharactersList = () => {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCharacter, setSelectedCharacter] = useState(null);

  const fetchCharacters = async () => {
    try {
      const { data, error } = await supabase
        .from('characters')
        .select('*')
        .order('id');

      if (error) throw error;
      setCharacters(data);
    } catch (err) {
      console.error('Supabase fetch error:', err);
      setCharacters([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCharacters();

    // ✅ Check Supabase v2 realtime availability
    if (!supabase.channel) return;

    const charactersChannel = supabase
      .channel('public:characters')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'characters' },
        (payload) => {
          if (payload.eventType === 'INSERT') setCharacters((prev) => [...prev, payload.new]);
          if (payload.eventType === 'UPDATE')
            setCharacters((prev) =>
              prev.map((c) => (c.id === payload.new.id ? payload.new : c))
            );
          if (payload.eventType === 'DELETE')
            setCharacters((prev) => prev.filter((c) => c.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => supabase.removeChannel(charactersChannel);
  }, []);

  const filteredCharacters = characters.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.species.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', padding: '1rem' }}>
      <div style={{ flex: '2 1 400px', minWidth: '300px' }}>
        <h2 style={{ color: colors.primary, marginBottom: '1rem' }}>Characters</h2>

        <input
          type="text"
          placeholder="Search characters..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.5rem 0.75rem',
            marginBottom: '1rem',
            borderRadius: '8px',
            border: `1px solid ${colors.primary}`,
            outline: 'none',
            fontSize: '0.95rem',
          }}
        />

        {loading ? (
          <p>Loading...</p>
        ) : filteredCharacters.length === 0 ? (
          <p>No characters found.</p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '1rem',
            }}
          >
            {filteredCharacters.map((c) => (
              <div
                key={c.id}
                onClick={() => setSelectedCharacter(c)}
                style={{
                  backgroundColor: colors.background,
                  padding: '1rem',
                  borderRadius: '12px',
                  border: `1px solid ${colors.primary}20`,
                  boxShadow: '0 8px 18px rgba(0,0,0,0.08)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 25px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 18px rgba(0,0,0,0.08)';
                }}
              >
                <h3 style={{ margin: '0 0 6px 0', color: colors.primary }}>
                  🍄 {c.name}
                </h3>
                <p style={{ margin: 0, fontWeight: '500' }}>{c.species}</p>
                <p style={{ marginTop: '4px', fontSize: '0.9rem', color: colors.textLight }}>
                  First appearance: {c.first_appearance || 'Unknown'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ flex: '1 1 300px', minWidth: '280px' }}>
        <AddCharacterForm onCharacterAdded={(newChar) => setCharacters((prev) => [...prev, newChar])} />
      </div>

      <CharacterModal character={selectedCharacter} onClose={() => setSelectedCharacter(null)} />
    </div>
  );
};

export default CharactersList;